import {
	API_SEARCH_DOCTOR,
	API_SEARCH_DOCTOR_SUCCESS,
	API_SEARCH_DOCTOR_FAIL,
	API_GET_DOCTOR_EXISTS,
	API_GET_DOCTOR_EXISTS_SUCCESS,
	API_GET_DOCTOR_EXISTS_FAIL,
	API_POST_DOCTOR,
	API_POST_DOCTOR_SUCCESS,
	API_POST_DOCTOR_FAIL,
	API_PATCH_DOCTOR,
	API_PATCH_DOCTOR_FAIL,
	API_PATCH_DOCTOR_SUCCESS,
	API_POST_COPY_PACK_TO_DOCTOR_SUCCESS,
	API_POST_COPY_PACK_TO_DOCTOR,
	API_POST_COPY_PACK_TO_DOCTOR_FAIL
} from '../actions/doctor';
import { toast } from "react-toastify";
import { createReducer } from '@reduxjs/toolkit';
import { errorHandler, ErrorAction } from 'utils/error';
import { getVersion } from "services/doctor";
import { DEFAULT_CLIENT, V2_CLIENT } from "config/clientConstants";

const toNumber = (value) => {
	if (value === null || value === undefined) return value;
	const num = typeof value === 'string' ? parseFloat(value) : Number(value);
	return isNaN(num) ? value : num;
};

const adaptDoctorResponse = (doctor, currentClient) => {
	try {
		if (!doctor || currentClient !== V2_CLIENT) {
			return doctor;
		}

		const adapted = { ...doctor };

		const fieldMap = {
			lastName: 'last_name',
			firstName: 'first_name',
			postalCode: 'postal_code',
			mylabId: 'mylab_id',
			silId: 'sil_id',
			autoFreePrescription: 'auto_free_prescription',
			createdAt: 'created_at',
		};

		Object.keys(fieldMap).forEach((camelKey) => {
			if (adapted[camelKey] !== undefined && adapted[fieldMap[camelKey]] === undefined) {
				adapted[fieldMap[camelKey]] = adapted[camelKey];
			}
		});

		return adapted;
	} catch (error) {
		console.error('Error in adaptDoctorResponse:', error);
		return doctor;
	}
};

const adaptDoctorPayloadResponse = (action, actionType) => {
	try {
		const payload = action.payload || {};
		const data = payload.data;
		const currentClient = action.meta?.previousAction?.payload?.client || getVersion(actionType).currentClient;

		if (!data) {
			return payload;
		}

		if (currentClient === V2_CLIENT) {
			if (Array.isArray(data)) {
				const adaptedData = data.map((item) => adaptDoctorResponse(item, currentClient));
				return {
					...payload,
					data: adaptedData,
				};
			} else if (typeof data === 'object' && data !== null) {
				if (data.results && Array.isArray(data.results)) {
					const adaptedResults = data.results.map((item) => adaptDoctorResponse(item, currentClient));
					return {
						...payload,
						data: {
							...data,
							results: adaptedResults,
						},
					};
				} else {
					const adaptedData = adaptDoctorResponse(data, currentClient);
					return {
						...payload,
						data: adaptedData,
					};
				}
			}
		}

		return payload;
	} catch (error) {
		console.error('Error in adaptDoctorPayloadResponse:', error);
		return action.payload || {};
	}
};

// @see NURSE-341 Here are initial values for doctors list
const initialState = {
	loading: false, // Loading flag
	list: [], // This is a list of MYLAB doctors
	exists: 0, // It is important to init this value to 0
	doctor: null // This is a NURSE doctor
};

const doctors = createReducer(initialState, {
	/** GET ALL **/
	[API_SEARCH_DOCTOR]: (state, action) => {
		state.loading = true;
		state.list = [];
	},
	[API_SEARCH_DOCTOR_FAIL]: (state, action) => {
		errorHandler('Docteur', ErrorAction.READ, action.error);
		state.loading = false;
	},
	[API_SEARCH_DOCTOR_SUCCESS]: (state, action) => {
		state.loading = false;
		// @see NURSE-341 Note that this search is done against MYLAB API
		// Also, an `exists` property, containing NURSE ID, is added to corresponding NURSE doctor, when available
		// @see https://gitlab.bionext.lu/hit/products/nurse/api/-/blob/develop/src/controllers/MyLabNewAPIController.ts#L737
		const adaptedPayload = adaptDoctorPayloadResponse(action, API_SEARCH_DOCTOR);
		state.list = adaptedPayload.data?.results || [];
	},

	/** EXISTS **/
	[API_GET_DOCTOR_EXISTS]: (state, action) => {
		state.loading = true;
		state.exists = 0; // It is important to reset this value to 0
	},
	[API_GET_DOCTOR_EXISTS_FAIL]: (state, action) => {
		state.loading = false;
		state.exists = 0;
	},
	[API_GET_DOCTOR_EXISTS_SUCCESS]: (state, action) => {
		state.loading = false;
		// @see NURSE-341 From a Doctor MYLAB ID, `exists` contains corresponding NURSE ID or 0
		// @see https://gitlab.bionext.lu/hit/products/nurse/api/-/blob/develop/src/controllers/DoctorController.ts#L71
		const adaptedPayload = adaptDoctorPayloadResponse(action, API_GET_DOCTOR_EXISTS);
		state.exists = adaptedPayload.data?.exists || 0;
	},

	/** POST DOCTOR **/
	[API_POST_DOCTOR]: (state, action) => {
		state.loading = true;
		state.doctor = null;
	},
	[API_POST_DOCTOR_FAIL]: (state, action) => {
		state.loading = false;
		state.doctor = null;
	},
	[API_POST_DOCTOR_SUCCESS]: (state, action) => {
		state.loading = false;
		const adaptedPayload = adaptDoctorPayloadResponse(action, API_POST_DOCTOR);
		state.doctor = adaptedPayload.data || null;
	},

	/** PATCH DOCTOR **/
	[API_PATCH_DOCTOR]: (state, action) => {
		state.loading = true;
		state.doctor = null;
	},
	[API_PATCH_DOCTOR_FAIL]: (state, action) => {
		state.loading = false;
		state.doctor = null;
	},
	[API_PATCH_DOCTOR_SUCCESS]: (state, action) => {
		state.loading = false;
		const adaptedPayload = adaptDoctorPayloadResponse(action, API_PATCH_DOCTOR);
		state.doctor = adaptedPayload.data || null;
	},
	[API_POST_COPY_PACK_TO_DOCTOR]: (state, action) => {
		state.loading = true;
	},
	[API_POST_COPY_PACK_TO_DOCTOR_SUCCESS]: (state, action) => {
		state.loading = false;
		toast.success("Le pack prescripteur a été copié.");

	},
	[API_POST_COPY_PACK_TO_DOCTOR_FAIL]: (state, action) => {
		state.loading = false;
		errorHandler('Copie Pack Prescripteur', ErrorAction.UPDATE, action.error);
	},
});

export default doctors;
